// src/mergeDeep.ts
function mergeDeep(first, second) {
  const result = { ...first };
  for (const key in second) {
    if (second[key] instanceof Object && key in first) {
      result[key] = mergeDeep(first[key], second[key]);
    } else {
      result[key] = second[key];
    }
  }
  return result;
}

// src/palette.ts
var palette = {
  blue: {
    dark: "#005AA3",
    darkActive: "#003E70",
    darkHover: "#004F8F",
    darker: "#004680",
    light: "#E8F4FD",
    lightActive: "#B4DBF8",
    lightHover: "#D0E9FB",
    normal: "#0172CB",
    normalActive: "#01508E",
    normalHover: "#0161AC"
  },
  cloud: {
    dark: "#E8EDF1",
    darker: "#BAC7D5",
    darkerActive: "#94A8BE",
    darkerHover: "#A6B6C8",
    light: "#F5F7F9",
    lightActive: "#D6DEE6",
    lightHover: "#E5EAEF",
    normal: "#EFF2F5",
    normalActive: "#CAD4DE",
    normalHover: "#DCE3E9"
  },
  green: {
    dark: "#2D7738",
    darkActive: "#1F5126",
    darkHover: "#276831",
    darker: "#235C2B",
    light: "#EBF4EC",
    lightActive: "#C3DFC7",
    lightHover: "#D7EAD9",
    normal: "#28A138",
    normalActive: "#1D7228",
    normalHover: "#238B31"
  },
  ink: {
    light: "#4F5E71",
    lightActive: "#324256",
    lightHover: "#3E4E63",
    lighter: "#697D95",
    lighterActive: "#4A617C",
    lighterHover: "#5D738E",
    normal: "#252A31",
    normalActive: "#0B0C0F",
    normalHover: "#181B20"
  },
  orange: {
    dark: "#BA5D00",
    darkActive: "#954A00",
    darkHover: "#A75400",
    darker: "#803F00",
    light: "#FCF2E6",
    lightActive: "#F5D7B3",
    lightHover: "#F9E5CC",
    normal: "#DF7B00",
    normalActive: "#B26200",
    normalHover: "#C96F00"
  },
  product: {
    dark: "#007F6D",
    darkActive: "#006657",
    darkHover: "#007060",
    darker: "#005C4E",
    light: "#E6F6F4",
    lightActive: "#B3E4DD",
    lightHover: "#CCEDE8",
    normal: "#00A58E",
    normalActive: "#008472",
    normalHover: "#009580"
  },
  red: {
    dark: "#970C0C",
    darkActive: "#6D0909",
    darkHover: "#890B0B",
    darker: "#760909",
    light: "#FAEAEA",
    lightActive: "#EEB9B9",
    lightHover: "#F4D2D2",
    normal: "#D21C1C",
    normalActive: "#9D1515",
    normalHover: "#B91919"
  },
  white: {
    normal: "#FFFFFF",
    normalActive: "#E7ECF1",
    normalHover: "#F1F4F7"
  }
};
var palette_default = palette;

// src/convertHexToRgba.ts
var convertHexToRgba = (color, opacity) => {
  const removeHash = color.replace("#", "");
  const hex = removeHash.length === 3 ? removeHash + removeHash : removeHash;
  const red = parseInt(hex.substring(0, 2), 16);
  const green = parseInt(hex.substring(2, 4), 16);
  const blue = parseInt(hex.substring(4, 6), 16);
  return `rgba(${red}, ${green}, ${blue}, ${opacity / 100})`;
};
var convertHexToRgba_default = convertHexToRgba;

// src/foundation.ts
var palette2 = {
  ...palette_default,
  social: {
    facebook: "#3B5998",
    facebookHover: "#385490",
    facebookActive: "#354F88"
  },
  bundle: {
    basic: "#D7331C",
    medium: "#3B1EB0"
  }
};
var base = {
  fontFamily: "'Roboto', -apple-system, '.SFNSText-Regular', 'San Francisco', 'Segoe UI', 'Helvetica Neue', 'Lucida Grande', sans-serif",
  fontSizeSm: "12px",
  fontSizeMd: "14px",
  fontSizeLg: "16px",
  borderRadius: "3px",
  sizeSm: "16px",
  sizeMd: "24px",
  sizeLg: "32px",
  sizeXl: "44px",
  size2xl: "52px",
  opacitySmall: "0.3",
  opacityMedium: "0.5",
  opacityLarge: "0.8",
  fontWeightNormal: "400",
  fontWeightMedium: "500",
  fontWeightBold: "700",
  space2xs: "4px",
  spaceXs: "8px",
  spaceSm: "12px",
  spaceMd: "16px",
  spaceLg: "24px",
  spaceXl: "32px",
  space2xl: "40px",
  space3xl: "52px",
  durationFast: "0.15s",
  durationNormal: "0.3s",
  durationSlow: "0.4s",
  transitionDefault: "ease-in-out",
  lineHeight: "1.4"
};
var foundation = { palette: palette2, base };
var foundation_default = foundation;

// src/index.ts
var getTokens = (customFoundation) => {
  const theme = customFoundation ? mergeDeep(foundation_default, customFoundation) : foundation_default;
  return {
    colorTextPrimary: theme.palette.ink.normal,
    colorTextSecondary: theme.palette.ink.light,
    colorTextError: theme.palette.red.normal,
    colorTextInfo: theme.palette.blue.normal,
    colorTextSuccess: theme.palette.green.normal,
    colorTextWarning: theme.palette.orange.normal,
    colorTextCritical: theme.palette.red.normal,
    colorTextWhite: theme.palette.white.normal,
    colorIconPrimary: theme.palette.ink.normal,
    colorIconSecondary: theme.palette.ink.light,
    colorIconTertiary: theme.palette.cloud.darker,
    colorIconInfo: theme.palette.blue.normal,
    colorIconSuccess: theme.palette.green.normal,
    colorIconWarning: theme.palette.orange.normal,
    colorIconCritical: theme.palette.red.normal,
    colorHeading: theme.palette.ink.normal,
    colorHeadingInverted: theme.palette.white.normal,
    colorTextLinkPrimary: theme.palette.product.dark,
    colorTextLinkPrimaryHover: theme.palette.product.normal,
    colorTextLinkSecondary: theme.palette.ink.normal,
    colorTextLinkSecondaryHover: theme.palette.product.normal,
    colorAlertIconSuccess: theme.palette.green.normal,
    colorTextAlertSuccess: theme.palette.green.dark,
    colorAlertIconInfo: theme.palette.blue.normal,
    colorTextAlertInfo: theme.palette.blue.dark,
    colorAlertIconWarning: theme.palette.orange.normal,
    colorTextAlertWarning: theme.palette.orange.dark,
    colorAlertIconCritical: theme.palette.red.normal,
    colorTextAlertCritical: theme.palette.red.dark,
    colorTextAlertLink: theme.palette.ink.normal,
    colorTextButtonFilled: theme.palette.white.normal,
    colorTextButtonFilledHover: theme.palette.white.normal,
    colorTextButtonFilledActive: theme.palette.white.normal,
    colorTextButtonPrimary: theme.palette.white.normal,
    colorTextButtonPrimaryHover: theme.palette.white.normal,
    colorTextButtonPrimaryActive: theme.palette.white.normal,
    colorTextButtonSecondary: theme.palette.ink.normal,
    colorTextButtonSecondaryHover: theme.palette.ink.normalHover,
    colorTextButtonSecondaryActive: theme.palette.ink.normalActive,
    colorTextButtonInfo: theme.palette.white.normal,
    colorTextButtonInfoHover: theme.palette.white.normal,
    colorTextButtonInfoActive: theme.palette.white.normal,
    colorTextButtonSuccess: theme.palette.white.normal,
    colorTextButtonSuccessHover: theme.palette.white.normal,
    colorTextButtonSuccessActive: theme.palette.white.normal,
    colorTextButtonWarning: theme.palette.white.normal,
    colorTextButtonWarningHover: theme.palette.white.normal,
    colorTextButtonWarningActive: theme.palette.white.normal,
    colorTextButtonCritical: theme.palette.white.normal,
    colorTextButtonCriticalHover: theme.palette.white.normal,
    colorTextButtonCriticalActive: theme.palette.white.normal,
    colorTextButtonFacebook: theme.palette.white.normal,
    colorTextButtonFacebookHover: theme.palette.white.normal,
    colorTextButtonFacebookActive: theme.palette.white.normal,
    colorTextButtonGoogle: theme.palette.ink.normal,
    colorTextButtonGoogleHover: theme.palette.ink.normalHover,
    colorTextButtonGoogleActive: theme.palette.ink.normalActive,
    colorTextButtonWhite: theme.palette.ink.normal,
    colorTextButtonWhiteHover: theme.palette.ink.normalHover,
    colorTextButtonWhiteActive: theme.palette.ink.normalActive,
    colorTextButtonPrimaryBordered: theme.palette.product.normal,
    colorTextButtonPrimaryBorderedHover: theme.palette.product.normalHover,
    colorTextButtonPrimaryBorderedActive: theme.palette.product.normalActive,
    colorTextButtonSecondaryBordered: theme.palette.ink.normal,
    colorTextButtonSecondaryBorderedHover: theme.palette.ink.normalHover,
    colorTextButtonSecondaryBorderedActive: theme.palette.ink.normalActive,
    colorTextButtonInfoBordered: theme.palette.blue.normal,
    colorTextButtonInfoBorderedHover: theme.palette.blue.normalHover,
    colorTextButtonInfoBorderedActive: theme.palette.blue.normalActive,
    colorTextButtonSuccessBordered: theme.palette.green.normal,
    colorTextButtonSuccessBorderedHover: theme.palette.green.normalHover,
    colorTextButtonSuccessBorderedActive: theme.palette.green.normalActive,
    colorTextButtonWarningBordered: theme.palette.orange.normal,
    colorTextButtonWarningBorderedHover: theme.palette.orange.normalHover,
    colorTextButtonWarningBorderedActive: theme.palette.orange.normalActive,
    colorTextButtonCriticalBordered: theme.palette.red.normal,
    colorTextButtonCriticalBorderedHover: theme.palette.red.normalHover,
    colorTextButtonCriticalBorderedActive: theme.palette.red.normalActive,
    colorTextButtonFacebookBordered: theme.palette.social.facebook,
    colorTextButtonFacebookBorderedHover: theme.palette.social.facebookHover,
    colorTextButtonFacebookBorderedActive: theme.palette.social.facebookActive,
    colorTextButtonGoogleBordered: theme.palette.ink.normal,
    colorTextButtonGoogleBorderedHover: theme.palette.ink.normalHover,
    colorTextButtonGoogleBorderedActive: theme.palette.ink.normalActive,
    colorTextButtonWhiteBordered: theme.palette.white.normal,
    colorTextButtonWhiteBorderedHover: theme.palette.white.normal,
    colorTextButtonWhiteBorderedActive: theme.palette.white.normal,
    colorTextButtonLinkPrimary: theme.palette.product.normal,
    colorTextButtonLinkPrimaryHover: theme.palette.product.darkHover,
    colorTextButtonLinkPrimaryActive: theme.palette.product.darkActive,
    colorTextButtonLinkSecondary: theme.palette.ink.normal,
    colorTextButtonLinkSecondaryHover: theme.palette.ink.normalHover,
    colorTextButtonLinkSecondaryActive: theme.palette.ink.normalActive,
    colorTextButtonLinkSecondaryCompactHover: theme.palette.product.normalHover,
    colorTextButtonLinkSecondaryCompactActive: theme.palette.product.normalActive,
    colorTextInput: theme.palette.ink.normal,
    colorTextInputPrefix: theme.palette.ink.light,
    colorTextInputDisabled: theme.palette.ink.lighter,
    colorTextBadgeNeutral: theme.palette.ink.normal,
    colorTextBadgeInfo: theme.palette.blue.normal,
    colorTextBadgeSuccess: theme.palette.green.normal,
    colorTextBadgeWarning: theme.palette.orange.normal,
    colorTextBadgeCritical: theme.palette.red.normal,
    colorTextBadgeDark: theme.palette.white.normal,
    colorTextBadgeWhite: theme.palette.ink.normal,
    colorTextLoading: theme.palette.cloud.darker,
    colorTextTable: theme.palette.ink.light,
    colorTextTag: theme.palette.ink.normal,
    colorTextTagSelected: theme.palette.cloud.normal,
    colorIconInput: theme.palette.cloud.darker,
    colorPlaceholderInput: theme.palette.ink.lighter,
    colorPlaceholderInputError: theme.palette.red.normal,
    colorPlaceholderInputFile: theme.palette.ink.light,
    colorPlaceholderInputFileError: theme.palette.red.normal,
    colorFormLabel: theme.palette.ink.normal,
    colorFormLabelFilled: theme.palette.ink.light,
    colorInfoCheckBoxRadio: theme.palette.ink.light,
    colorIconCheckboxRadio: theme.palette.product.normal,
    colorIconCheckboxRadioDisabled: theme.palette.cloud.darker,
    colorIconRadioButton: theme.palette.product.normal,
    colorIconRadioButtonDisabled: theme.palette.ink.lighter,
    colorStopoverArrow: theme.palette.ink.light,
    fontFamily: theme.base.fontFamily,
    backgroundBody: theme.palette.cloud.light,
    backgroundModal: theme.palette.white.normal,
    backgroundCard: theme.palette.white.normal,
    backgroundCarrierLogo: "transparent",
    backgroundCountryFlag: "transparent",
    backgroundButtonPrimary: theme.palette.product.normal,
    backgroundButtonPrimaryHover: theme.palette.product.normalHover,
    backgroundButtonPrimaryActive: theme.palette.product.normalActive,
    backgroundButtonSecondary: theme.palette.cloud.dark,
    backgroundButtonSecondaryHover: theme.palette.cloud.normalHover,
    backgroundButtonSecondaryActive: theme.palette.cloud.normalActive,
    backgroundButtonFacebook: theme.palette.social.facebook,
    backgroundButtonFacebookHover: theme.palette.social.facebookHover,
    backgroundButtonFacebookActive: theme.palette.social.facebookActive,
    backgroundButtonGoogle: theme.palette.cloud.light,
    backgroundButtonGoogleHover: theme.palette.cloud.lightHover,
    backgroundButtonGoogleActive: theme.palette.cloud.lightActive,
    backgroundButtonInfo: theme.palette.blue.normal,
    backgroundButtonInfoHover: theme.palette.blue.normalHover,
    backgroundButtonInfoActive: theme.palette.blue.normalActive,
    backgroundButtonSuccess: theme.palette.green.normal,
    backgroundButtonSuccessHover: theme.palette.green.normalHover,
    backgroundButtonSuccessActive: theme.palette.green.normalActive,
    backgroundButtonWarning: theme.palette.orange.normal,
    backgroundButtonWarningHover: theme.palette.orange.normalHover,
    backgroundButtonWarningActive: theme.palette.orange.normalActive,
    backgroundButtonCritical: theme.palette.red.normal,
    backgroundButtonCriticalHover: theme.palette.red.normalHover,
    backgroundButtonCriticalActive: theme.palette.red.normalActive,
    backgroundButtonWhite: theme.palette.white.normal,
    backgroundButtonWhiteHover: theme.palette.cloud.light,
    backgroundButtonWhiteActive: theme.palette.cloud.lightHover,
    backgroundButtonBundleBasic: `linear-gradient(to top right, #E13E3B 0%, #E87E09 100%)`,
    backgroundButtonBundleBasicHover: `linear-gradient(to top right, #BD2825 0%, #D67000 100%)`,
    backgroundButtonBundleBasicActive: `linear-gradient(to top right, #9F1816 0%, #C36802 100%)`,
    backgroundButtonBundleMedium: `linear-gradient(to top right, #3719AB 0%, #8539DB 100%)`,
    backgroundButtonBundleMediumHover: `linear-gradient(to top right, #2D1393 0%, #7343AA 100%)`,
    backgroundButtonBundleMediumActive: `linear-gradient(to top right, #250F79 0%, #5A3485 100%)`,
    backgroundButtonBundleTop: `linear-gradient(to top right, #2D2D2E 0%, #696E73 100%)`,
    backgroundButtonBundleTopHover: `linear-gradient(to top right, #171718 0%, #51575C 100%)`,
    backgroundButtonBundleTopActive: `linear-gradient(to top right, #101011 0%, #51575C)`,
    backgroundButtonBordered: "transparent",
    backgroundButtonBorderedHover: theme.palette.cloud.light,
    backgroundButtonBorderedActive: theme.palette.cloud.light,
    backgroundButtonWhiteBordered: "transparent",
    backgroundButtonWhiteBorderedHover: convertHexToRgba_default(theme.palette.white.normal, 20),
    backgroundButtonWhiteBorderedActive: convertHexToRgba_default(theme.palette.white.normal, 20),
    backgroundButtonLinkPrimary: "transparent",
    backgroundButtonLinkPrimaryHover: theme.palette.cloud.lightHover,
    backgroundButtonLinkPrimaryActive: theme.palette.cloud.lightActive,
    backgroundButtonLinkSecondary: "transparent",
    backgroundButtonLinkSecondaryHover: theme.palette.cloud.lightHover,
    backgroundButtonLinkSecondaryActive: theme.palette.cloud.lightActive,
    backgroundInput: theme.palette.white.normal,
    backgroundInputDisabled: theme.palette.cloud.normal,
    backgroundAlertSuccess: theme.palette.green.light,
    backgroundAlertInfo: theme.palette.blue.light,
    backgroundAlertWarning: theme.palette.orange.light,
    backgroundAlertCritical: theme.palette.red.light,
    backgroundBadgeNeutral: theme.palette.cloud.light,
    backgroundBadgeInfo: theme.palette.blue.light,
    backgroundBadgeSuccess: theme.palette.green.light,
    backgroundBadgeWarning: theme.palette.orange.light,
    backgroundBadgeCritical: theme.palette.red.light,
    backgroundBadgeDark: theme.palette.ink.normal,
    backgroundBadgeWhite: theme.palette.white.normal,
    backgroundBadgeBundleBasic: `linear-gradient(to top right, #E13E3B 0%, #E87E09 100%)`,
    backgroundBadgeBundleMedium: `linear-gradient(to top right, #3719AB 0%, #8539DB 100%)`,
    backgroundBadgeBundleTop: `linear-gradient(to top right, #2D2D2E 0%, #696E73 100%)`,
    backgroundServiceLogo: "transparent",
    backgroundIllustration: "transparent",
    backgroundSeparator: theme.palette.cloud.normal,
    backgroundTableShadowLeft: `linear-gradient(to left, transparent, ${convertHexToRgba_default(
      theme.palette.cloud.darker,
      23
    )})`,
    backgroundTableShadowRight: `linear-gradient(to right, transparent, ${convertHexToRgba_default(
      theme.palette.cloud.darker,
      23
    )})`,
    backgroundTable: theme.palette.white.normal,
    backgroundTableEven: theme.palette.cloud.light,
    backgroundTableHover: theme.palette.cloud.normal,
    backgroundTag: theme.palette.cloud.light,
    backgroundTagSelected: theme.palette.ink.normal,
    backgroundTagHover: theme.palette.cloud.lightHover,
    backgroundTagSelectedHover: theme.palette.ink.normalHover,
    backgroundTagActive: theme.palette.cloud.lightActive,
    backgroundTagSelectedActive: theme.palette.ink.normalActive,
    backgroundTooltip: theme.palette.white.normal,
    backgroundTooltipLargeMobile: theme.palette.blue.dark,
    fontSizeHeadingDisplay: "40px",
    fontSizeHeadingDisplaySubtitle: "22px",
    fontSizeHeadingTitle1: "28px",
    fontSizeHeadingTitle2: "22px",
    fontSizeHeadingTitle3: "16px",
    fontSizeHeadingTitle4: theme.base.fontSizeMd,
    fontSizeHeadingTitle5: theme.base.fontSizeSm,
    fontSizeTextNormal: theme.base.fontSizeMd,
    fontSizeTextLarge: theme.base.fontSizeLg,
    fontSizeTextSmall: theme.base.fontSizeSm,
    fontSizeMessage: theme.base.fontSizeMd,
    fontSizeButtonLarge: theme.base.fontSizeLg,
    fontSizeButtonNormal: theme.base.fontSizeMd,
    fontSizeButtonSmall: theme.base.fontSizeSm,
    fontSizeInputNormal: theme.base.fontSizeMd,
    fontSizeInputSmall: theme.base.fontSizeMd,
    fontSizeFormLabel: theme.base.fontSizeMd,
    fontSizeFormFeedback: theme.base.fontSizeSm,
    borderRadiusCircle: "50%",
    borderRadiusNormal: theme.base.borderRadius,
    borderRadiusLarge: "6px",
    borderRadiusSmall: "2px",
    borderRadiusBadge: "12px",
    zIndexDefault: "1",
    zIndexSticky: "100",
    zIndexDrawer: "815",
    zIndexModalOverlay: "800",
    zIndexModal: "825",
    zIndexOnTheTop: "900",
    widthIconSmall: theme.base.sizeSm,
    heightIconSmall: theme.base.sizeSm,
    widthIconMedium: theme.base.sizeMd,
    heightIconMedium: theme.base.sizeMd,
    widthIconLarge: theme.base.sizeLg,
    heightIconLarge: theme.base.sizeLg,
    heightInputNormal: theme.base.sizeXl,
    heightInputLarge: theme.base.size2xl,
    heightInputSmall: theme.base.sizeLg,
    heightButtonNormal: theme.base.sizeXl,
    heightButtonLarge: theme.base.size2xl,
    heightButtonSmall: theme.base.sizeLg,
    heightRadioButton: "20px",
    widthRadioButton: "20px",
    heightCheckbox: "20px",
    widthCheckbox: "20px",
    heightCountryFlag: "auto",
    widthCountryFlag: theme.base.sizeMd,
    heightBadge: theme.base.sizeMd,
    widthBadgeCircled: theme.base.sizeMd,
    heightIllustrationSmall: "90px",
    heightIllustrationMedium: "200px",
    heightServiceLogoSmall: "12px",
    heightServiceLogoMedium: theme.base.sizeMd,
    heightServiceLogoLarge: "48px",
    heightSeparator: "1px",
    heightInputGroupSeparatorSmall: theme.base.sizeSm,
    heightInputGroupSeparatorNormal: theme.base.sizeMd,
    widthModalSmall: "540px",
    widthModalNormal: "740px",
    widthModalLarge: "900px",
    widthModalExtraLarge: "1280px",
    widthStopoverArrow: "36px",
    heightStopoverArrow: "7px",
    widthBreakpointMediumMobile: 414,
    widthBreakpointLargeMobile: 576,
    widthBreakpointTablet: 768,
    widthBreakpointDesktop: 992,
    widthBreakpointLargeDesktop: 1200,
    borderColorInput: theme.palette.cloud.darker,
    borderColorInputHover: theme.palette.ink.lighterHover,
    borderColorInputActive: theme.palette.ink.lighterActive,
    borderColorInputFocus: theme.palette.blue.normal,
    borderColorInputError: theme.palette.red.normal,
    borderColorInputErrorHover: theme.palette.red.normalHover,
    borderColorInputErrorFocus: theme.palette.red.normal,
    borderColorTableCell: theme.palette.cloud.darker,
    borderColorCard: theme.palette.cloud.normal,
    borderColorModal: theme.palette.cloud.normal,
    borderColorButtonPrimaryBordered: theme.palette.product.normal,
    borderColorButtonPrimaryBorderedHover: theme.palette.product.normalHover,
    borderColorButtonPrimaryBorderedActive: theme.palette.product.normalActive,
    borderColorButtonSecondaryBordered: theme.palette.ink.normal,
    borderColorButtonSecondaryBorderedHover: theme.palette.ink.normalHover,
    borderColorButtonSecondaryBorderedActive: theme.palette.ink.normalActive,
    borderColorButtonFacebookBordered: theme.palette.social.facebook,
    borderColorButtonFacebookBorderedHover: theme.palette.social.facebookHover,
    borderColorButtonFacebookBorderedActive: theme.palette.social.facebookActive,
    borderColorButtonGoogleBordered: theme.palette.ink.normal,
    borderColorButtonGoogleBorderedHover: theme.palette.ink.normalHover,
    borderColorButtonGoogleBorderedActive: theme.palette.ink.normalActive,
    borderColorButtonInfoBordered: theme.palette.blue.normal,
    borderColorButtonInfoBorderedHover: theme.palette.blue.normalHover,
    borderColorButtonInfoBorderedActive: theme.palette.blue.normalActive,
    borderColorButtonSuccessBordered: theme.palette.green.normal,
    borderColorButtonSuccessBorderedHover: theme.palette.green.normalHover,
    borderColorButtonSuccessBorderedActive: theme.palette.green.normalActive,
    borderColorButtonWarningBordered: theme.palette.orange.normal,
    borderColorButtonWarningBorderedHover: theme.palette.orange.normalHover,
    borderColorButtonWarningBorderedActive: theme.palette.orange.normalActive,
    borderColorButtonCriticalBordered: theme.palette.red.normal,
    borderColorButtonCriticalBorderedHover: theme.palette.red.normalHover,
    borderColorButtonCriticalBorderedActive: theme.palette.red.normalActive,
    borderColorButtonWhiteBordered: theme.palette.white.normal,
    borderColorButtonWhiteBorderedHover: theme.palette.white.normal,
    borderColorButtonWhiteBorderedActive: theme.palette.white.normal,
    borderColorCheckboxRadio: theme.palette.cloud.darker,
    borderColorCheckboxRadioError: theme.palette.red.normal,
    borderColorCheckboxRadioHover: theme.palette.ink.light,
    borderColorCheckboxRadioActive: theme.palette.ink.normal,
    borderColorCheckboxRadioFocus: theme.palette.blue.normal,
    borderColorTable: theme.palette.cloud.normal,
    borderColorTableHead: theme.palette.cloud.darker,
    borderColorTag: theme.palette.cloud.normal,
    borderColorTagFocus: theme.palette.blue.normal,
    borderStyleCard: "solid",
    borderWidthCard: "1px",
    borderStyleInput: "solid",
    borderWidthInput: "1px",
    borderWidthInputFocus: "2px",
    opacityOverlay: theme.base.opacityLarge,
    opacityButtonDisabled: theme.base.opacitySmall,
    opacityRadioButtonDisabled: theme.base.opacityMedium,
    opacityCheckboxDisabled: theme.base.opacityMedium,
    fontWeightNormal: theme.base.fontWeightNormal,
    fontWeightMedium: theme.base.fontWeightMedium,
    fontWeightBold: theme.base.fontWeightBold,
    fontWeightLinks: theme.base.fontWeightMedium,
    fontWeightHeadingDisplay: theme.base.fontWeightBold,
    fontWeightHeadingDisplaySubtitle: theme.base.fontWeightNormal,
    fontWeightHeadingTitle1: theme.base.fontWeightBold,
    fontWeightHeadingTitle2: theme.base.fontWeightMedium,
    fontWeightHeadingTitle3: theme.base.fontWeightMedium,
    fontWeightHeadingTitle4: theme.base.fontWeightMedium,
    fontWeightHeadingTitle5: theme.base.fontWeightBold,
    fontWeightTableHead: theme.base.fontWeightBold,
    paddingAlert: theme.base.spaceMd,
    paddingAlertWithIcon: theme.base.spaceSm,
    paddingBadge: `0 ${theme.base.spaceXs}`,
    paddingButtonWithoutText: "0",
    paddingButtonSmall: `0 ${theme.base.spaceSm}`,
    paddingButtonNormal: `0 ${theme.base.spaceMd}`,
    paddingButtonLarge: "0 28px",
    paddingButtonSmallWithIcons: `0 ${theme.base.spaceXs}`,
    paddingButtonNormalWithIcons: `0 ${theme.base.spaceSm}`,
    paddingButtonLargeWithIcons: `0 ${theme.base.spaceMd}`,
    paddingButtonSmallWithLeftIcon: `0 ${theme.base.spaceSm} 0 ${theme.base.spaceXs}`,
    paddingButtonNormalWithLeftIcon: `0 ${theme.base.spaceMd} 0 ${theme.base.spaceSm}`,
    paddingButtonLargeWithLeftIcon: `0 28px 0 ${theme.base.spaceMd}`,
    paddingButtonSmallWithRightIcon: `0 ${theme.base.spaceXs} 0 ${theme.base.spaceSm}`,
    paddingButtonNormalWithRightIcon: `0 ${theme.base.spaceSm} 0 ${theme.base.spaceMd}`,
    paddingButtonLargeWithRightIcon: `0 ${theme.base.spaceMd} 0 28px`,
    paddingTextareaSmall: `${theme.base.spaceXs} ${theme.base.spaceSm}`,
    paddingTextareaNormal: theme.base.spaceSm,
    paddingInputSmall: `0 ${theme.base.spaceSm}`,
    paddingInputNormal: `0 ${theme.base.spaceSm}`,
    marginButtonIconSmall: theme.base.spaceXs,
    marginButtonIconNormal: theme.base.spaceXs,
    marginButtonIconLarge: theme.base.spaceSm,
    marginButtonGroup: "0 1px 0 0",
    marginTopFormFeedback: "2px",
    marginBottomSelectSuffix: "2px",
    paddingInputFile: "0 0 0 6px",
    paddingLeftSelectPrefix: "48px",
    paddingLoading: theme.base.spaceSm,
    paddingTable: `${theme.base.spaceSm} ${theme.base.spaceMd}`,
    paddingTableCompact: `${theme.base.spaceXs} ${theme.base.spaceSm}`,
    paddingTag: `${theme.base.spaceXs}`,
    paddingTagWithIcon: `6px ${theme.base.spaceXs} 6px 6px`,
    paddingTagRemovable: `6px 6px 6px ${theme.base.spaceXs}`,
    paddingTagRemovableWithIcon: "6px",
    marginBadgeIcon: `0 ${theme.base.space2xs} 0 0`,
    spaceXXXSmall: "2px",
    spaceXXSmall: theme.base.space2xs,
    spaceXSmall: theme.base.spaceXs,
    spaceSmall: theme.base.spaceSm,
    spaceMedium: theme.base.spaceMd,
    spaceLarge: theme.base.spaceLg,
    spaceXLarge: theme.base.spaceXl,
    spaceXXLarge: theme.base.space2xl,
    spaceXXXLarge: theme.base.space3xl,
    durationFast: theme.base.durationFast,
    durationNormal: theme.base.durationNormal,
    durationSlow: theme.base.durationSlow,
    modifierScaleButtonActive: 0.95,
    modifierScaleCheckboxRadioActive: 0.95,
    lineHeightText: theme.base.lineHeight,
    lineHeightTextSmall: "16px",
    lineHeightTextNormal: "20px",
    lineHeightTextLarge: "24px",
    lineHeightHeading: "1.2",
    lineHeightHeadingDisplay: "44px",
    lineHeightHeadingDisplaySubtitle: "28px",
    lineHeightHeadingTitle1: "36px",
    lineHeightHeadingTitle2: "28px",
    lineHeightHeadingTitle3: "24px",
    lineHeightHeadingTitle4: "20px",
    lineHeightHeadingTitle5: "20px",
    textDecorationTextLinkPrimary: "underline",
    textDecorationTextLinkPrimaryHover: "none",
    textDecorationTextLinkSecondary: "underline",
    textDecorationTextLinkSecondaryHover: "none",
    boxShadowButtonFocus: `0 0 4px 1px ${convertHexToRgba_default(theme.palette.blue.normal, 30)}`,
    boxShadowAction: `0px 0px 2px 0px ${convertHexToRgba_default(
      theme.palette.ink.normal,
      16
    )}, 0px 1px 4px 0px ${convertHexToRgba_default(theme.palette.ink.normal, 12)}`,
    boxShadowActionActive: `0px 1px 4px 0px ${convertHexToRgba_default(
      theme.palette.ink.normal,
      16
    )}, 0px 4px 8px 0px ${convertHexToRgba_default(theme.palette.ink.normal, 12)}`,
    boxShadowFixed: `0px 0px 2px 0px ${convertHexToRgba_default(
      theme.palette.ink.normal,
      16
    )}, 0px 2px 4px 0px ${convertHexToRgba_default(theme.palette.ink.normal, 12)}`,
    boxShadowFixedReverse: `0px 0px 2px 0px ${convertHexToRgba_default(
      theme.palette.ink.normal,
      16
    )}, 0px -2px 4px 0px ${convertHexToRgba_default(theme.palette.ink.normal, 12)}`,
    boxShadowRaised: `0px 4px 8px 0px ${convertHexToRgba_default(
      theme.palette.ink.normal,
      16
    )}, 0px 8px 24px 0px ${convertHexToRgba_default(theme.palette.ink.normal, 24)}`,
    boxShadowRaisedReverse: `0px -4px 8px 0px ${convertHexToRgba_default(
      theme.palette.ink.normal,
      16
    )}, 0px -8px 24px 0px ${convertHexToRgba_default(theme.palette.ink.normal, 24)}`,
    boxShadowOverlay: `0px 12px 24px -4px ${convertHexToRgba_default(
      theme.palette.ink.normal,
      24
    )}, 0px 8px 60px 0px ${convertHexToRgba_default(theme.palette.ink.normal, 32)}`,
    paletteProductLight: theme.palette.product.light,
    paletteProductLightHover: theme.palette.product.lightHover,
    paletteProductLightActive: theme.palette.product.lightActive,
    paletteProductNormal: theme.palette.product.normal,
    paletteProductNormalHover: theme.palette.product.normalHover,
    paletteProductNormalActive: theme.palette.product.normalActive,
    paletteProductDark: theme.palette.product.dark,
    paletteProductDarkHover: theme.palette.product.darkHover,
    paletteProductDarkActive: theme.palette.product.darkActive,
    paletteProductDarker: theme.palette.product.darker,
    paletteWhite: theme.palette.white.normal,
    paletteWhiteHover: theme.palette.cloud.light,
    paletteWhiteActive: theme.palette.cloud.lightHover,
    paletteCloudLight: theme.palette.cloud.light,
    paletteCloudLightHover: theme.palette.cloud.lightHover,
    paletteCloudLightActive: theme.palette.cloud.lightActive,
    paletteCloudNormal: theme.palette.cloud.normal,
    paletteCloudNormalHover: theme.palette.cloud.normalHover,
    paletteCloudNormalActive: theme.palette.cloud.normalActive,
    paletteCloudDark: theme.palette.cloud.dark,
    paletteCloudDarker: theme.palette.cloud.darker,
    paletteCloudDarkerHover: theme.palette.cloud.darkerHover,
    paletteCloudDarkerActive: theme.palette.cloud.darkerActive,
    paletteInkLighter: theme.palette.ink.lighter,
    paletteInkLighterHover: theme.palette.ink.lighterHover,
    paletteInkLighterActive: theme.palette.ink.lighterActive,
    paletteInkLight: theme.palette.ink.light,
    paletteInkLightHover: theme.palette.ink.lightHover,
    paletteInkLightActive: theme.palette.ink.lightActive,
    paletteInkNormal: theme.palette.ink.normal,
    paletteInkNormalHover: theme.palette.ink.normalHover,
    paletteInkNormalActive: theme.palette.ink.normalActive,
    paletteOrangeLight: theme.palette.orange.light,
    paletteOrangeLightHover: theme.palette.orange.lightHover,
    paletteOrangeLightActive: theme.palette.orange.lightActive,
    paletteOrangeNormal: theme.palette.orange.normal,
    paletteOrangeNormalHover: theme.palette.orange.normalHover,
    paletteOrangeNormalActive: theme.palette.orange.normalActive,
    paletteOrangeDark: theme.palette.orange.dark,
    paletteOrangeDarkHover: theme.palette.orange.darkHover,
    paletteOrangeDarkActive: theme.palette.orange.darkActive,
    paletteOrangeDarker: theme.palette.orange.darker,
    paletteRedLight: theme.palette.red.light,
    paletteRedLightHover: theme.palette.red.lightHover,
    paletteRedLightActive: theme.palette.red.lightActive,
    paletteRedNormal: theme.palette.red.normal,
    paletteRedNormalHover: theme.palette.red.normalHover,
    paletteRedNormalActive: theme.palette.red.normalActive,
    paletteRedDark: theme.palette.red.dark,
    paletteRedDarkHover: theme.palette.red.darkHover,
    paletteRedDarkActive: theme.palette.red.darkActive,
    paletteRedDarker: theme.palette.red.darker,
    paletteGreenLight: theme.palette.green.light,
    paletteGreenLightHover: theme.palette.green.lightHover,
    paletteGreenLightActive: theme.palette.green.lightActive,
    paletteGreenNormal: theme.palette.green.normal,
    paletteGreenNormalHover: theme.palette.green.normalHover,
    paletteGreenNormalActive: theme.palette.green.normalActive,
    paletteGreenDark: theme.palette.green.dark,
    paletteGreenDarkHover: theme.palette.green.darkHover,
    paletteGreenDarkActive: theme.palette.green.darkActive,
    paletteGreenDarker: theme.palette.green.darker,
    paletteBlueLight: theme.palette.blue.light,
    paletteBlueLightHover: theme.palette.blue.lightHover,
    paletteBundleBasic: theme.palette.bundle.basic,
    paletteBundleMedium: theme.palette.bundle.medium,
    paletteBlueLightActive: theme.palette.blue.lightActive,
    paletteBlueNormal: theme.palette.blue.normal,
    paletteBlueNormalHover: theme.palette.blue.normalHover,
    paletteBlueNormalActive: theme.palette.blue.normalActive,
    paletteBlueDark: theme.palette.blue.dark,
    paletteBlueDarkHover: theme.palette.blue.darkHover,
    paletteBlueDarkActive: theme.palette.blue.darkActive,
    paletteBlueDarker: theme.palette.blue.darker,
    paletteSocialFacebook: theme.palette.social.facebook,
    paletteSocialFacebookHover: theme.palette.social.facebookHover,
    paletteSocialFacebookActive: theme.palette.social.facebookActive
  };
};
var defaultTokens = getTokens();
var fromPlainObject = (themePaletteColors) => {
  const theme = {
    palette: {
      product: {
        light: themePaletteColors.productLight,
        lightHover: themePaletteColors.productLightHover,
        lightActive: themePaletteColors.productLightActive,
        normal: themePaletteColors.productNormal,
        normalHover: themePaletteColors.productNormalHover,
        normalActive: themePaletteColors.productNormalActive,
        dark: themePaletteColors.productDark,
        darkHover: themePaletteColors.productDarkHover || foundation_default.palette.product.darkHover,
        darkActive: themePaletteColors.productDarkActive || foundation_default.palette.product.darkActive,
        darker: themePaletteColors.productDarker || foundation_default.palette.product.darker
      }
    }
  };
  return getTokens(theme);
};

export { convertHexToRgba_default as convertHexToRgba, defaultTokens, foundation_default as foundation, fromPlainObject, getTokens, palette_default as palette };
